import { useState, useEffect } from 'react';

export const useCalculations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const SESSION_STORAGE_KEY = 'alasfire_calculations';

  // Hesaplamaları sessionStorage'dan yükle
  useEffect(() => {
    loadCalculationsFromStorage();
  }, []);

  // SessionStorage'dan hesaplamaları yükleme
  const loadCalculationsFromStorage = () => {
    try {
      const storedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setCalculationHistory(parsedData);
      }
    } catch (error) {
      console.error("Hesaplamaları yüklerken hata oluştu:", error);
      setError("Kayıtlı hesaplamalar yüklenemedi");
    }
  };

  // Hesaplamayı kaydet (sessionStorage)
  const saveCalculation = async (calculationData, updateMode = false) => {
    setLoading(true);
    setError(null);
    
    try {
      // Mevcut hesaplamaları al
      let calculations = [];
      try {
        const storedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
        calculations = storedData ? JSON.parse(storedData) : [];
      } catch (error) {
        console.error("Mevcut hesaplamalar yüklenemedi:", error);
        calculations = [];
      }
      
      // Aynı proje ve oda adı kontrolü
      const projectName = calculationData.project_name || '';
      const roomName = calculationData.room_name || '';
      
      const existingCalculationIndex = calculations.findIndex(calc => 
        (calc.project_name || '') === projectName && 
        (calc.room_name || '') === roomName
      );
      
      if (existingCalculationIndex !== -1 && !updateMode) {
        // Aynı oda zaten kayıtlı, kullanıcıya seçenek sun
        return { 
          success: false, 
          error: 'duplicate_room',
          existingId: calculations[existingCalculationIndex].id,
          message: 'Bu proje ve oda adı zaten kayıtlı!' 
        };
      }
      
      // Her hesaplamaya benzersiz bir ID ve tarih ekle
      const timestamp = new Date().getTime();
      
      if (existingCalculationIndex !== -1 && updateMode) {
        // Mevcut kaydı güncelle
        const updatedCalculation = {
          ...calculations[existingCalculationIndex],
          ...calculationData,
          updatedAt: new Date().toISOString(),
          timestamp: calculations[existingCalculationIndex].timestamp // Orijinal timestamp'i koru
        };
        
        calculations[existingCalculationIndex] = updatedCalculation;
        
        // SessionStorage'a kaydet
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(calculations));
        
        // State'i güncelle
        setCalculationHistory(calculations);
        
        return { success: true, data: updatedCalculation, updated: true };
      } else {
        // Yeni hesaplama ekle
        const newCalculation = {
          id: `calc_${timestamp}`,
          timestamp,
          createdAt: new Date().toISOString(),
          ...calculationData
        };
        
        // Yeni hesaplamayı başa ekle (en son kaydedilenler üstte)
        calculations = [newCalculation, ...calculations];
        
        // SessionStorage'a kaydet
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(calculations));
        
        // State'i güncelle
        setCalculationHistory(calculations);
        
        return { success: true, data: newCalculation, updated: false };
      }
    } catch (error) {
      console.error("Hesaplama kaydedilirken hata oluştu:", error);
      setError("Hesaplama kaydedilemedi");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Hesaplama geçmişini getir (sessionStorage)
  const fetchCalculationHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      loadCalculationsFromStorage();
      return { success: true, data: calculationHistory };
    } catch (error) {
      console.error("Hesaplama geçmişi yüklenemedi:", error);
      setError("Hesaplama geçmişi alınamadı");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Hesaplama sil
  const deleteCalculation = async (calculationId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Mevcut hesaplamaları al
      const storedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
      let calculations = storedData ? JSON.parse(storedData) : [];
      
      // İlgili hesaplamayı filtrele
      calculations = calculations.filter(calc => calc.id !== calculationId);
      
      // SessionStorage'a kaydet
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(calculations));
      
      // State'i güncelle
      setCalculationHistory(calculations);
      
      return { success: true };
    } catch (error) {
      console.error("Hesaplama silinirken hata oluştu:", error);
      setError("Hesaplama silinemedi");
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Tüm hesaplamaları temizle
  const clearAllCalculations = () => {
    try {
      // SessionStorage'dan hesaplamaları sil
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      
      // State'i güncelle
      setCalculationHistory([]);
      
      return { success: true };
    } catch (error) {
      console.error("Hesaplamalar temizlenirken hata oluştu:", error);
      setError("Hesaplamalar temizlenemedi");
      return { success: false, error: error.message };
    }
  };

  return {
    loading,
    error,
    calculationHistory,
    saveCalculation,
    fetchCalculationHistory,
    deleteCalculation,
    clearAllCalculations
  };
}; 